import React, { Fragment, memo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { catalogApi } from '@app/store';
import { CatalogDTO } from '@app/common';
import { Button, Form, Panel } from '@app/components';
import { CatalogForm } from './CatalogForm';
import { CatalogFormValues, useCatalogFormSubmit } from './catalog-form.helpers';
import { useToast, useTriptych } from '@app/hooks';
import { CatalogCard } from './CatalogCard';
import { InputFileRef } from '@app/components/form/InputFile';
import { extractApiGenericError } from '@app/helpers';
import { useAppSelector } from '@app/store/hooks';
import { triptychSelectors } from '@app/store/slices/triptych.slice';
import type { ValeursDeCopie } from '../Duplication/cles-privees';

type Props = { copie?: ValeursDeCopie<CatalogFormValues> };

// Le formulaire porte le useForm : il est remonté (clé) quand on passe d'une copie
// au vierge ou d'une copie à une autre, car react-hook-form ne relit
// `defaultValues` qu'au montage.
const CatalogCreateForm = memo(({ copie }: Props) => {
  const toast = useToast();

  const triptych = useTriptych();

  const cardImageRef = useRef<InputFileRef>();

  const onSubmit = useCatalogFormSubmit();

  const [create, { isLoading, error: apiErrors }] = catalogApi.useCreateOneCatalogsMutation();

  const valeursVierges: CatalogFormValues = {
    name: '',
    ticketInfos: {
      logo: undefined,
      bottomLines: ''
    },
    image: null,
    catalogToConnectors: [],
    catalogToPromotions: [],
    catalogToShops: [],
    catalogToTags: [],
    catalogToPacks: [],
    catalogToProducts: [],
    catalogToExcludedSteps: [],
    catalogToExcludedOptions: []
  };

  const defaultValues = copie?.copieDe ? copie : valeursVierges;

  const formMethods = useForm<CatalogFormValues>({
    resolver: classValidatorResolver(CatalogDTO),
    defaultValues,
  });

  const onCreate = async (formData: FormData) => {
    try {
      const catalog = await create(formData as any).unwrap();

      triptych.close({ mode: 'UPDATE', data: catalog });

      toast.success('Votre catalogue a bien été ajouté.')
    } catch(error) {
      const errorMessage = extractApiGenericError(error) ?? 'Une erreur est survenue lors de la création de votre catalogue.';
      toast.error(errorMessage);
    }
  }

  return (
    <Fragment>
      <CatalogCard
        formMethods={formMethods}
        toggle={() => triptych.toggle()}
        onOpenImagePicker={() => cardImageRef.current?.open()} />

      <Panel.Content>

        <Form formMethods={formMethods} apiErrors={apiErrors}>

          <Panel.FormContainer>

            <CatalogForm ref={cardImageRef} />

          </Panel.FormContainer>

          <Panel.Actions>
            <Button
              title="Retour"
              grow
              large
              bordered
              onPress={() => triptych.close()} />

            <Button
              loading={isLoading}
              title="Valider"
              onPress={() => onSubmit(formMethods, onCreate)}
              grow
              large />
          </Panel.Actions>

        </Form>

      </Panel.Content>
    </Fragment>
  );
});

export const CatalogCreate = memo(() => {
  // Dupliquer : la fiche ouvre ce panneau avec les valeurs de copie dans le triptyque.
  const copie = useAppSelector(triptychSelectors.selectData) as ValeursDeCopie<CatalogFormValues> | undefined;

  return <CatalogCreateForm key={copie?.copieDe ?? 'creation'} copie={copie?.copieDe ? copie : undefined} />;
});
